// Super-Admin only â€” manage the DeviceType catalog (homeautomation.DeviceType),
// the "product type" master the C# desktop tool edits. List every device type,
// edit any, or add a new one.
//
// Endpoints (backend already exposes them):
//   GetAllDeviceTypeDetails (list â€” joins ImageData + OTABinFile) Â·
//   AddDeviceType (body IS the row; upserts on DeviceName) Â·
//   UpdateDeviceType ({ data, where: "DeviceID = N" }).
//
// The list row carries columns from three joined tables. We only ever write the
// DeviceType columns back â€” DEVICE_TYPE_COLUMNS is that whitelist, so the UPDATE
// never touches an ImageData/OTABinFile column and fails.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, Card, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Grid,
  Tooltip, Alert, MenuItem, Chip, FormControlLabel, Checkbox, InputAdornment,
  Divider, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import MemoryIcon from '@mui/icons-material/Memory';
import { getDeviceTypes, addDeviceType, updateDeviceType } from '../../../core/api/api';

// ---- The DeviceType table columns we allow writing back (add/update). ----
// Anything NOT here (ImageId, OTABinFileId FKs, joined ImageData/OTABinFile
// columns, server timestamps) is left untouched.
const NUMERIC_COLS = new Set([
  'DeviceID', 'Outputs', 'On_off', 'Dimming', 'Inputs', 'SortOrder',
  'Mrp', 'Warranty', 'Discount', 'PriceWithWifi', 'PriceWithoutWifi',
  'BlankPcb', 'Assembled', 'Tested', 'Packed',
]);
const FLAG_COLS = [
  ['IsMasterDevice', 'Master device'],
  ['CurrentSensor', 'Current sensor'],
  ['SupportsWiFi', 'Supports Wi-Fi'],
  ['SupportsVoice', 'Supports voice'],
  ['SupportsScene', 'Supports scenes'],
  ['ViewOnWebsite', 'Show on website'],
  ['AllowTestingTuya', 'Allow Tuya testing'],
  ['BellPush', 'Bell push'],
  ['Curtain', 'Curtain'],
  ['TouchButtons', 'Touch buttons'],
  ['RGBChannels', 'RGB channels'],
];
const DEVICE_TYPE_OPTS = ['Switch', 'Fan', 'Curtain', 'ColorController', 'SmartLock', 'Sensor', 'Other'];
const ALEXA_TYPE_OPTS = ['Light', 'Fan', 'Curtain', 'Smartlock', 'Others'];

// Every DeviceType column this screen manages â€” used to build the write payload.
const DEVICE_TYPE_COLUMNS = [
  'DeviceID', 'DeviceInfoId', 'DeviceName', 'DeviceType', 'AlexaType', 'DefaultValue',
  'Outputs', 'On_off', 'Dimming', 'Inputs', 'SortOrder', 'Status', 'Discription',
  'Mrp', 'Warranty', 'Discount', 'PriceWithWifi', 'PriceWithoutWifi',
  'BlankPcb', 'Assembled', 'Tested', 'Packed',
  'OTAUpdateLink', 'OTAUpdateLinkNuvo', 'LocalCodeLink', 'LocalCodeLinkNuvo',
  'ManualPdfLink', 'AppUrlLink', 'ImageLocation', 'ImageType',
  ...FLAG_COLS.map(([k]) => k),
];

const num = (v) => (v === '' || v == null ? '' : Number(v));

export default function DeviceTypesScreen() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeviceTypes();
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.DeviceName, r.DeviceType, r.AlexaType, String(r.DeviceID)]
        .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [list, query]);

  const blankForm = () => {
    const f = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => {
      if (NUMERIC_COLS.has(k)) f[k] = 0;
      else if (FLAG_COLS.some(([fk]) => fk === k)) f[k] = 0;
      else f[k] = '';
    });
    f.DeviceID = '';           // let the admin assign it explicitly
    f.DeviceType = 'Switch';
    f.AlexaType = 'Light';
    f.ImageType = 'Product';
    return f;
  };

  const openNew = () => { setMsg(''); setEditing(false); setForm(blankForm()); setOpen(true); };
  const openEdit = (r) => {
    setMsg('');
    setEditing(true);
    const f = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => { f[k] = r[k] ?? (NUMERIC_COLS.has(k) ? 0 : ''); });
    setForm(f);
    setOpen(true);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const buildPayload = () => {
    const data = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => {
      let v = form[k];
      if (NUMERIC_COLS.has(k)) v = v === '' || v == null ? 0 : Number(v);
      else if (FLAG_COLS.some(([fk]) => fk === k)) v = v ? 1 : 0;
      else v = v ?? '';
      data[k] = v;
    });
    // On add, drop DeviceID if the admin left it blank so the DB can assign it.
    if (!editing && (form.DeviceID === '' || form.DeviceID == null)) delete data.DeviceID;
    return data;
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const data = buildPayload();
      if (editing) {
        await updateDeviceType(data, `DeviceID = ${form.DeviceID}`);
      } else {
        await addDeviceType(data);
      }
      await load();
      setOpen(false);
    } catch (e) {
      setMsg('Save failed â€” check the connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const valid = form && String(form.DeviceName || '').trim().length > 0;

  const numField = (k, label, xs = 6, sm = 3) => (
    <Grid item xs={xs} sm={sm} key={k}>
      <TextField
        label={label} type="number" size="small" fullWidth
        value={form[k] ?? ''}
        onChange={(e) => patch({ [k]: e.target.value === '' ? '' : num(e.target.value) })}
      />
    </Grid>
  );
  const textField = (k, label, opts = {}) => (
    <Grid item xs={12} sm={opts.sm ?? 6} key={k}>
      <TextField
        label={label} size="small" fullWidth
        value={form[k] ?? ''}
        onChange={(e) => patch({ [k]: e.target.value })}
        {...(opts.tf || {})}
      />
    </Grid>
  );

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>Device Types</Typography>
          <Tooltip title="Add device type">
            <IconButton color="inherit" onClick={openNew}><AddIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search name / type / IDâ€¦"
            value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loadingâ€¦' : `${filtered.length} of ${list.length} device types`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add device type</Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Card} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['ID', 'Device Name', 'Type', 'Alexa', 'Out', 'On/Off', 'Dim', 'In', 'MRP', ''].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align={i >= 4 && i <= 8 ? 'center' : 'left'}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.DeviceID} hover sx={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                    <TableCell><Chip size="small" icon={<MemoryIcon />} label={r.DeviceID} /></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.DeviceName || 'â€”'}</TableCell>
                    <TableCell>{r.DeviceType || 'â€”'}</TableCell>
                    <TableCell>{r.AlexaType || 'â€”'}</TableCell>
                    <TableCell align="center">{r.Outputs}</TableCell>
                    <TableCell align="center">{r.On_off}</TableCell>
                    <TableCell align="center">{r.Dimming}</TableCell>
                    <TableCell align="center">{r.Inputs}</TableCell>
                    <TableCell align="center">{r.Mrp || 'â€”'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No device types found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add / Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? `Edit Device Type â€” ${form?.DeviceName || ''} (ID ${form?.DeviceID})` : 'Add Device Type'}
        </DialogTitle>
        <DialogContent dividers>
          {form && (
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              {msg && <Alert severity="error">{msg}</Alert>}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Identity</Typography>
                <Grid container spacing={1.5}>
                  {textField('DeviceName', 'Device Name', { sm: 6 })}
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label="Device ID" type="number" size="small" fullWidth
                      value={form.DeviceID ?? ''} disabled={editing}
                      helperText={editing ? 'Primary key' : 'Blank = auto'}
                      onChange={(e) => patch({ DeviceID: e.target.value === '' ? '' : num(e.target.value) })}
                    />
                  </Grid>
                  {numField('SortOrder', 'Sort Order', 6, 3)}
                  <Grid item xs={6} sm={3}>
                    <TextField select label="Device Type" size="small" fullWidth
                      value={form.DeviceType || ''} onChange={(e) => patch({ DeviceType: e.target.value })}>
                      {DEVICE_TYPE_OPTS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField select label="Alexa Type" size="small" fullWidth
                      value={form.AlexaType || ''} onChange={(e) => patch({ AlexaType: e.target.value })}>
                      {ALEXA_TYPE_OPTS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                  </Grid>
                  {textField('DeviceInfoId', 'Device Info Id', { sm: 3 })}
                  {textField('Status', 'Status', { sm: 3 })}
                  {textField('Discription', 'Description', { sm: 12, tf: { multiline: true, minRows: 1 } })}
                </Grid>
              </Box>

              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Channels</Typography>
                <Grid container spacing={1.5}>
                  {numField('Outputs', 'Outputs')}
                  {numField('On_off', 'On/Off relays')}
                  {numField('Dimming', 'Dimming')}
                  {numField('Inputs', 'Inputs')}
                  {textField('DefaultValue', 'Default Value (e.g. 0,0,0,0)', { sm: 12 })}
                </Grid>
              </Box>

              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Capabilities</Typography>
                <Grid container spacing={0}>
                  {FLAG_COLS.map(([k, label]) => (
                    <Grid item xs={6} sm={4} md={3} key={k}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={!!Number(form[k])}
                          onChange={(e) => patch({ [k]: e.target.checked ? 1 : 0 })} />}
                        label={<Typography variant="body2">{label}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Pricing</Typography>
                <Grid container spacing={1.5}>
                  {numField('Mrp', 'MRP')}
                  {numField('PriceWithWifi', 'Price (with Wi-Fi)')}
                  {numField('PriceWithoutWifi', 'Price (without Wi-Fi)')}
                  {numField('Discount', 'Discount')}
                  {numField('Warranty', 'Warranty (months)')}
                </Grid>
              </Box>

              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Production stages</Typography>
                <Grid container spacing={1.5}>
                  {numField('BlankPcb', 'Blank PCB')}
                  {numField('Assembled', 'Assembled')}
                  {numField('Tested', 'Tested')}
                  {numField('Packed', 'Packed')}
                </Grid>
              </Box>

              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Links & image</Typography>
                <Grid container spacing={1.5}>
                  {textField('OTAUpdateLink', 'OTA Link')}
                  {textField('OTAUpdateLinkNuvo', 'OTA Link (Nuvo)')}
                  {textField('LocalCodeLink', 'Local Code Link')}
                  {textField('LocalCodeLinkNuvo', 'Local Code Link (Nuvo)')}
                  {textField('ManualPdfLink', 'Manual PDF Link')}
                  {textField('AppUrlLink', 'App URL Link')}
                  {textField('ImageLocation', 'Image file')}
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Image Type" size="small" fullWidth
                      value={form.ImageType || ''} onChange={(e) => patch({ ImageType: e.target.value })}>
                      {['Product', 'Compulsory'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!valid || saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
